'use client';

import { eccedenzaPeriodo, fmtNum } from '@slims/shared/calculations';
import type * as React from 'react';

import { Row, Stack, Text } from '../../atoms';
import { buildPeriodTabs, PERIOD_CURRENT } from '../../lib/period-tabs';
import { BodySkeleton, DashGrid, SegmentedTabs, TONE_VAR, type WidgetTone } from '../../molecules';
import { PageTitle } from '../../templates';
import {
	EnergyOverviewCard,
	EnergyOverviewSkeleton,
	type EnergyOverviewTotals,
} from './incentives-energy-overview';

export interface IncentivesConfigParams {
	/** Percentuale di contributo PNRR della CER: abbassa la soglia dal 55% al 45%. */
	pnrrGrantPct?: number | null;
	/** Percentuale di potenza preesistente, la cui condivisa non è incentivabile. */
	preEsistentePct?: number | null;
}

export interface IncentivesGseViewProps {
	/** Parametri di configurazione che decidono la quota incentivabile. */
	config: IncentivesConfigParams;
	/**
	 * Totali di energia **del periodo selezionato**, non dell'anno: le schede in
	 * cima cambiano la finestra e il cablaggio rilegge i totali con quella.
	 */
	totals: { totalCondivisa: number; totalImmessa: number; totalPrelevata?: number };
	/**
	 * I sei numeri della panoramica energetica. Ogni voce omessa ricade sul
	 * corrispondente totale del periodo o sulla stima interna.
	 */
	energyOverview?: Partial<EnergyOverviewTotals>;
	/** Periodo mostrato: `null` = mese in corso, altrimenti `YYYY-MM`. */
	period: string | null;
	/** Cambio di finestra temporale dalle schede in cima. */
	onPeriodChange: (period: string | null) => void;
	/**
	 * Almeno un impianto ha il contributo in conto capitale (soglia 45%), dagli
	 * impianti e non dal solo valore di CER (INB-313). Assente finché il
	 * cablaggio non l'ha caricato: vale `config.pnrrGrantPct`.
	 */
	hasPnrrGrant?: boolean;
	/**
	 * Quota (0–1) dell'immessa dagli impianti con contributo in conto capitale:
	 * la soglia 55%/45% vale per insieme di impianti (INB-324). Assente: vale per
	 * tutta la configurazione.
	 */
	quotaImmessaPnrr?: number | null;
	/** Un solo flag per l'intero pannello: le tile si sbloccano insieme. */
	loading?: boolean;
}

/**
 * Pagina «Energia condivisa» (ex «Incentivi», rotta `/incentives`): la panoramica energetica del periodo — quanta energia
 * condivisa è incentivabile e quanta eccedentaria — in kWh.
 */
const IncentivesGseView = ({
	config,
	totals,
	energyOverview,
	period,
	onPeriodChange,
	hasPnrrGrant: hasPnrrGrantProp,
	quotaImmessaPnrr,
	loading = false,
}: IncentivesGseViewProps) => {
	const periodTabs = buildPeriodTabs(new Date());
	const tabs = (
		<SegmentedTabs
			items={periodTabs}
			value={period ?? PERIOD_CURRENT}
			onChange={(value) => onPeriodChange(value === PERIOD_CURRENT ? null : value)}
			ariaLabel="Periodo degli incentivi"
		/>
	);

	// Le schede del periodo restano vere e cliccabili anche in caricamento: la
	// finestra è una scelta dell'utente, non un dato che deve ancora arrivare.
	if (loading) {
		return (
			<div>
				{tabs}
				<EnergyOverviewSkeleton />
			</div>
		);
	}

	const hasRealData = totals.totalCondivisa > 0;
	const sharedKwh = totals.totalCondivisa;

	// Energia incentivata ≠ energia condivisa (D.M. 414/2023 Art. 4): solo gli
	// impianti entrati in esercizio dopo il 16/12/2021 accedono alla tariffa
	// premio, con il tetto del 30% di capacità preesistente.
	const preEsistenteFraction = Math.min((config.preEsistentePct ?? 0) / 100, 0.3);
	const incentivataKwh = Math.round(sharedKwh * (1 - preEsistenteFraction));

	// Soglia di incentivabilità — 55% standard, 45% col contributo in conto
	// capitale (Art. 8 § 3), per insieme di impianti dove la quota è nota
	// (RO §2.2.2.1.3, INB-324). Quel che la supera è eccedentario.
	const hasPnrrGrant = hasPnrrGrantProp ?? (config.pnrrGrantPct ?? 0) > 0;
	const { eccedenzaKwh } = hasRealData
		? eccedenzaPeriodo({
				energiaIncentivabileKwh: incentivataKwh,
				energiaImmessaKwh: totals.totalImmessa,
				hasPnrrGrant,
				quotaImmessaPnrr,
			})
		: { eccedenzaKwh: 0 };
	// Incentivabile = condivisa al netto della quota preesistente **e** di quella
	// eccedentaria: sono due esclusioni diverse (l'impianto vecchio, la soglia).
	const incentivabileKwh = Math.max(0, incentivataKwh - eccedenzaKwh);

	return (
		<div>
			{tabs}
			<EnergyOverviewCard
				totals={{
					prodotta: energyOverview?.prodotta ?? null,
					prelevata: energyOverview?.prelevata ?? totals.totalPrelevata ?? null,
					immessa: energyOverview?.immessa ?? (totals.totalImmessa || null),
					condivisa: energyOverview?.condivisa ?? (totals.totalCondivisa || null),
					incentivabile: energyOverview?.incentivabile ?? (hasRealData ? incentivabileKwh : null),
					eccedentaria: energyOverview?.eccedentaria ?? (hasRealData ? eccedenzaKwh : null),
				}}
			/>
		</div>
	);
};
IncentivesGseView.displayName = 'IncentivesGseView';

// ─── Panoramica configurazioni ───────────────────────────────────────────────

/**
 * Vista attiva della pagina Incentivi quando più configurazioni sono
 * selezionate: `multi` è la griglia «Panoramica» (una card per configurazione),
 * `single` è il pannello completo di una configurazione sola. Stesso schema di
 * Dashboard, Monitoraggio e Previsione.
 */
export type IncentivesViewMode = 'multi' | 'single';

/** Voce del picker di configurazione, in modalità singola. */
export interface IncentivesConfigPickerItem {
	id: string;
	cerNome: string;
	configNome: string;
}

/** Dati di una card della «Panoramica» — i numeri del mese in corso di una configurazione sola. */
export interface IncentivesConfigCardData {
	id: string;
	cerNome: string;
	configNome: string;
	comune?: string | null;
	areaCode?: string | null;
	/** Energia condivisa del mese in corso, kWh. */
	condivisaKwh: number;
	/** Soci attivi (non receduti). */
	sociCount: number;
}

export interface IncentivesConfigCardProps {
	data: IncentivesConfigCardData;
	onClick?: () => void;
}

function ConfigMetricCell({
	label,
	value,
	tone,
}: {
	label: string;
	value: string;
	tone: WidgetTone;
}) {
	return (
		<div className="rounded-[7px] border border-border bg-background px-[9px] py-[7px]">
			<div className="mb-0.5 text-[9px] font-extrabold uppercase tracking-wide text-text-muted">
				{label}
			</div>
			<div className="font-mono text-sm font-extrabold" style={{ color: TONE_VAR[tone] }}>
				{value}
			</div>
		</div>
	);
}

/** Card compatta di una configurazione nella «Panoramica» degli Incentivi: i numeri del mese in corso, cliccabile per aprirne il pannello completo. */
export function IncentivesConfigCard({ data, onClick }: IncentivesConfigCardProps) {
	return (
		<button
			type="button"
			onClick={onClick}
			className="w-full cursor-pointer rounded-[11px] border border-border bg-card px-3.5 py-[13px] text-left transition-[border-color,transform] duration-150 hover:-translate-y-0.5 hover:border-accent-purple"
		>
			<div className="mb-3 flex items-start justify-between gap-2">
				<div className="min-w-0">
					<div className="mb-1 truncate text-[9.5px] font-extrabold uppercase tracking-wide text-accent">
						{data.cerNome}
					</div>
					<div className="truncate text-sm font-extrabold leading-tight">{data.configNome}</div>
					<div className="mt-[3px] truncate text-[11px] text-text-muted">
						{data.comune}
						{data.areaCode && (
							<>
								{' · '}
								<span className="font-mono text-accent">{data.areaCode}</span>
							</>
						)}
					</div>
				</div>
				<span className="inline-flex flex-none items-center gap-1.5">
					<span className="h-2 w-2 animate-pulse-dot rounded-full bg-accent-green" />
					<span className="font-mono text-[9px] font-extrabold text-accent-green">MESE</span>
				</span>
			</div>
			<div className="mb-3 grid grid-cols-2 gap-2">
				<ConfigMetricCell
					label="Energia Condivisa"
					value={`${fmtNum(data.condivisaKwh)} kWh`}
					tone="purple"
				/>
				<ConfigMetricCell label="Soci attivi" value={fmtNum(data.sociCount)} tone="accent" />
			</div>
			<Text as="span" variant="caption" weight="bold" tone="accent">
				clic → incentivi
			</Text>
		</button>
	);
}

export interface IncentivesConfigCardSkeletonProps {
	/** Nome della CER — l'unico dato già noto (arriva dallo scope-tree, non dal fetch di questa card): resta nitido, il resto sfoca. */
	cerNome: string;
}

/** Guscio di {@link IncentivesConfigCard} in caricamento — stessa cornice/padding della card reale. */
export function IncentivesConfigCardSkeleton({ cerNome }: IncentivesConfigCardSkeletonProps) {
	return (
		<div
			className="w-full rounded-[11px] border border-border bg-card px-3.5 py-[13px] text-left"
			// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- guscio di card generico (`div`), stesso pattern di `PanelCardSkeleton`.
			role="status"
			aria-label={`Caricamento configurazione - ${cerNome}`}
		>
			<div className="mb-3 flex items-start justify-between gap-2">
				<div className="min-w-0 flex-1">
					<div className="mb-1 truncate text-[9.5px] font-extrabold uppercase tracking-wide text-accent">
						{cerNome}
					</div>
					<BodySkeleton className="mb-1.5 h-4 w-32" />
					<BodySkeleton className="h-3 w-40" />
				</div>
				<BodySkeleton className="h-4 w-12 flex-none rounded-full" />
			</div>
			<div className="mb-3 grid grid-cols-2 gap-2">
				{Array.from({ length: 2 }).map((_, i) => (
					<div
						key={i}
						className="rounded-[7px] border border-border bg-background px-[9px] py-[7px]"
					>
						<BodySkeleton className="mb-1 h-2.5 w-20" />
						<BodySkeleton className="h-4 w-16" />
					</div>
				))}
			</div>
			<BodySkeleton className="h-3 w-28" />
		</div>
	);
}

const INCENTIVES_VIEW_TABS: { value: IncentivesViewMode; label: string }[] = [
	{ value: 'multi', label: 'Panoramica' },
	{ value: 'single', label: 'Configurazione singola' },
];

export interface IncentivesPageViewProps {
	/** Corpo della pagina in modalità singola: il pannello di una configurazione. */
	content: React.ReactNode;
	/**
	 * Vista attiva. Assente (o `configs` con un solo elemento): niente toggle,
	 * si mostra solo `content` — il comportamento di prima della «Panoramica».
	 */
	view?: IncentivesViewMode;
	onViewChange?: (view: IncentivesViewMode) => void;
	/** Configurazioni disponibili per il picker di modalità singola. */
	configs?: IncentivesConfigPickerItem[];
	/** Configurazione scelta nel picker, in modalità singola. */
	selectedConfigId?: string | null;
	onSelectConfig?: (id: string) => void;
	/**
	 * Contenuto della griglia «Panoramica»: una {@link IncentivesConfigCard} per
	 * configurazione, fornita dal wiring — ognuna fa il proprio fetch, la vista
	 * non sa come si ottiene.
	 */
	overviewGridSlot?: React.ReactNode;
}

/**
 * Intestazione della pagina Incentivi: il titolo e — con più configurazioni in
 * ambito — il toggle «Panoramica»/«Configurazione singola».
 */
export function IncentivesPageView({
	content,
	view,
	onViewChange,
	configs = [],
	selectedConfigId = null,
	onSelectConfig,
	overviewGridSlot,
}: IncentivesPageViewProps) {
	// Il toggle esiste solo con più di una configurazione in ambito: con una
	// sola, la pagina è quella di prima.
	const configToggle =
		view != null && onViewChange != null && configs.length > 1 ? { view, onViewChange } : null;
	const selectedConfig = configs.find((c) => c.id === selectedConfigId) ?? null;
	const pageTitle =
		configToggle?.view === 'single' && selectedConfig
			? selectedConfig.configNome
			: 'Energia condivisa';

	return (
		<div>
			<Row gap="sm" align="center" justify="between" wrap mb="md">
				<PageTitle>{pageTitle}</PageTitle>
				{configToggle && (
					<Stack gap="sm" align="stretch">
						<SegmentedTabs
							items={INCENTIVES_VIEW_TABS}
							value={configToggle.view}
							onChange={configToggle.onViewChange}
							ariaLabel="Vista degli incentivi"
							className="mb-0"
						/>
						{configToggle.view === 'single' && onSelectConfig && (
							<select
								value={selectedConfigId ?? ''}
								onChange={(e) => onSelectConfig(e.target.value)}
								aria-label="Configurazione"
								className="min-w-60 rounded-[7px] border border-border bg-card px-2.5 py-1.5 text-[13px] text-text-primary outline-none focus:border-accent"
							>
								{configs.map((c) => (
									<option key={c.id} value={c.id}>
										{c.cerNome} · {c.configNome}
									</option>
								))}
							</select>
						)}
					</Stack>
				)}
			</Row>
			{configToggle?.view === 'multi' ? (
				// `@container` come nella Dashboard (`DashPanel`): senza, le container
				// query `@2xl:` di `DashGrid layout="configs"` non scattano mai e le
				// card restano incolonnate.
				<div className="@container">
					<Text as="div" variant="label" tone="muted" className="mb-2.5">
						Configurazioni selezionate · clicca una card per gli incentivi
					</Text>
					<DashGrid layout="configs">{overviewGridSlot}</DashGrid>
				</div>
			) : (
				content
			)}
		</div>
	);
}
IncentivesPageView.displayName = 'IncentivesPageView';

export { IncentivesGseView };
